export const FOCUS_LIMITS = { cells: 400, note: 200, focuses: 60 };
/** The words the composer says (design/22 Q4): so nobody types a secret into a note. */
export const NOTE_IS_PUBLIC = 'A note is visible to everyone at the table.';
export const FOCUS_KINDS = ['focus.set', 'focus.clear', 'focus.clearAll'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isIndex = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 10_000;
export function parseCells(v) {
    if (!Array.isArray(v) || v.length < 1 || v.length > FOCUS_LIMITS.cells)
        return null;
    const out = [];
    const seen = new Set();
    for (const c of v) {
        if (typeof c !== 'object' || c === null)
            return null;
        const { i, j } = c;
        if (!isIndex(i) || !isIndex(j))
            return null;
        const key = `${i},${j}`;
        if (seen.has(key))
            return null;
        seen.add(key);
        out.push({ i, j });
    }
    return out;
}
export function parseFocus(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { id, ownerId, cells, note, posted } = v;
    if (!isUuid(id) || !isUuid(ownerId))
        return null;
    const parsed = parseCells(cells);
    if (!parsed)
        return null;
    if (note !== undefined && (typeof note !== 'string' || note.length > FOCUS_LIMITS.note))
        return null;
    if (posted !== undefined && typeof posted !== 'boolean')
        return null;
    return { id, ownerId, cells: parsed, note: typeof note === 'string' ? note.trim() : '', posted: posted === true };
}
export function parseFocusBody(b) {
    switch (b.kind) {
        case 'focus.set': {
            const focus = parseFocus(b.focus);
            return focus ? { kind: 'focus.set', focus } : null;
        }
        case 'focus.clear': return isUuid(b.id) ? { kind: 'focus.clear', id: b.id } : null;
        case 'focus.clearAll': return { kind: 'focus.clearAll' };
        default: return null;
    }
}
/** Focuses from an older snapshot, or none. */
export function normaliseFocuses(v) {
    const out = {};
    if (typeof v !== 'object' || v === null)
        return out;
    for (const f of Object.values(v)) {
        const p = parseFocus(f);
        if (p)
            out[p.id] = p;
    }
    return out;
}
export function reduceFocuses(focuses, body) {
    switch (body.kind) {
        case 'focus.set': {
            const f = body.focus;
            return { ...focuses, [f.id]: { ...f, cells: f.cells.map((c) => ({ ...c })) } };
        }
        case 'focus.clear': {
            if (!focuses[body.id])
                return focuses;
            const next = { ...focuses };
            delete next[body.id];
            return next;
        }
        case 'focus.clearAll': return {};
        default: return focuses;
    }
}
/** Whether a person may clear a focus (design/08 §5): its owner, or the DM. */
export const mayClearFocus = (f, viewer) => viewer.role === 'dm' || f.ownerId === viewer.id;
/** The bounding box of a focus's cells, in cell indices — for the arrow and for "go there". */
export function cellBounds(cells) {
    let i0 = Infinity;
    let j0 = Infinity;
    let i1 = -Infinity;
    let j1 = -Infinity;
    for (const c of cells) {
        i0 = Math.min(i0, c.i);
        j0 = Math.min(j0, c.j);
        i1 = Math.max(i1, c.i);
        j1 = Math.max(j1, c.j);
    }
    return { i0, j0, i1, j1 };
}
/** Every cell inside a box of cell indices, inclusive, capped at the limit. */
export function cellsInBox(a, b) {
    const out = [];
    const i0 = Math.min(a.i, b.i);
    const i1 = Math.max(a.i, b.i);
    const j0 = Math.min(a.j, b.j);
    const j1 = Math.max(a.j, b.j);
    for (let j = j0; j <= j1; j += 1)
        for (let i = i0; i <= i1; i += 1) {
            if (out.length >= FOCUS_LIMITS.cells)
                return out;
            out.push({ i, j });
        }
    return out;
}
/** Add or remove one cell from a draft (clicking squares individually, design/08 §5). */
export function toggleCell(cells, c) {
    const have = cells.some((x) => x.i === c.i && x.j === c.j);
    if (have)
        return cells.filter((x) => !(x.i === c.i && x.j === c.j));
    return cells.length >= FOCUS_LIMITS.cells ? cells : [...cells, c];
}
//# sourceMappingURL=focus.js.map